import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TESTIMONIALS } from "@/data/site";
import { Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { LazyYouTube } from "./LazyYouTube";

export const Testimonials = () => {
    const [i, setI] = useState(0);
    const t = TESTIMONIALS[i];
    const next = () => setI((i + 1) % TESTIMONIALS.length);
    const prev = () =>
        setI((i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);

    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            className="relative py-12 md:py-16 bg-[#0A0A0A] overflow-hidden"
        >
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none bg-[radial-gradient(circle_at_30%_50%,#D4AF37,transparent_60%)]" />

            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 relative">
                <div className="text-center mb-14">
                    <span className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                        / 10 — Student Success
                    </span>
                    <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                        Real Words.{" "}
                        <span className="gold-text-gradient">
                            Real Moguls.
                        </span>
                    </h2>
                </div>

                {/* Featured YouTube spotlight video */}
                <div className="max-w-4xl mx-auto mb-12">
                    <div className="relative aspect-video w-full overflow-hidden border border-[#D4AF37]/30 bg-black">
                        <LazyYouTube
                            videoId="WQ4EH5A5a0s"
                            title="Young Mogul Spotlight"
                            testId="testimonials-video"
                        />
                    </div>
                </div>

                <div className="relative max-w-4xl mx-auto">
                    <Quote
                        className="absolute -top-8 -left-4 text-[#D4AF37]/30"
                        size={80}
                    />

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.45 }}
                            className="glass-gold p-10 md:p-16 text-center relative"
                            data-testid="testimonial-card"
                        >
                            <p className="font-display text-2xl md:text-3xl lg:text-4xl uppercase tracking-tight leading-tight text-white max-w-3xl mx-auto">
                                "{t.quote}"
                            </p>

                            <div className="mt-10 flex flex-col items-center gap-2">
                                <div className="h-px w-12 bg-[#D4AF37]" />
                                <div className="font-display text-xl uppercase tracking-wide text-[#D4AF37]">
                                    {t.name}
                                </div>
                                <div className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                                    {t.cohort}
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    <div className="mt-8 flex items-center justify-center gap-4">
                        <button
                            data-testid="testimonial-prev"
                            onClick={prev}
                            className="h-12 w-12 border border-white/20 grid place-items-center text-white hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] transition-all"
                            aria-label="Previous testimonial"
                        >
                            <ArrowLeft size={18} />
                        </button>
                        <div className="flex gap-2">
                            {TESTIMONIALS.map((t, idx) => (
                                <button
                                    key={`dot-${t.author}`}
                                    onClick={() => setI(idx)}
                                    data-testid={`testimonial-dot-${idx}`}
                                    className={`h-1 transition-all ${
                                        idx === i
                                            ? "w-10 bg-[#D4AF37]"
                                            : "w-5 bg-white/20 hover:bg-white/40"
                                    }`}
                                />
                            ))}
                        </div>
                        <button
                            data-testid="testimonial-next"
                            onClick={next}
                            className="h-12 w-12 border border-white/20 grid place-items-center text-white hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] transition-all"
                            aria-label="Next testimonial"
                        >
                            <ArrowRight size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};
